import {ChangeDetectionStrategy, Component, inject, signal} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Store} from '@ngrx/store';
import {filter, Observable, Subscription, take} from 'rxjs';
import {ScheduleQueryParams} from 'src/app/schedule/shared/model/schedule-query-params';
import {User} from '../../../core/model/user';
import {selectActiveUser} from '../../../core/store/active-user/active-user.selector';
import {
  GetAutoSkeds,
  UpdateScheduleTimezoneAction,
  UpdateSkedsWithNewTimezoneAction,
} from '../../../core/store/schedule/schedule.action';
import {selectScheduleAutoSkeds, selectScheduleQueryParams} from '../../../core/store/schedule/schedule.selector';
import {UpdateUserProfileAction} from '../../../core/store/users/users.action';
import {AutoSked} from '../../../schedule/shared/model/autosked';
import {MessageService} from '../../../services/message.service';
import {OphButtonModule} from '../../design/oph-button/oph-button.module';
import {OphCheckboxComponent} from '../../design/oph-checkbox/oph-checkbox.component';
import {OphXButtonComponent} from '../../design/oph-x-button/oph-x-button.component';
import {RruleService} from '../../services/rrule.service';

export interface TimezoneWarningDialogData {
  newTimezone: string;
  currentTimezone: string;
}

@Component({
  selector: 'timezone-warning-dialog',
  standalone: true,
  imports: [OphButtonModule, OphCheckboxComponent, OphXButtonComponent],
  templateUrl: './timezone-warning-dialog.component.html',
  styleUrl: './timezone-warning-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimezoneWarningDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<TimezoneWarningDialogComponent>);
  private readonly store$ = inject(Store);
  private readonly messageService = inject(MessageService);
  private readonly rruleService = inject(RruleService);
  readonly data = inject<TimezoneWarningDialogData>(MAT_DIALOG_DATA);

  autoSkeds$: Observable<AutoSked[]> = this.store$.select(selectScheduleAutoSkeds);
  public readonly queryParams$: Observable<ScheduleQueryParams> = this.store$.select(selectScheduleQueryParams);
  activeUser$: Observable<User> = this.store$.select(selectActiveUser);

  subscriptions = new Subscription();

  loadingGetAutoSkeds = signal(true);
  loadingSwtich = signal(false);
  changeSkeds = signal(false);
  showTooltip = signal(false);

  get newTimezone(): string {
    return this.data.newTimezone;
  }

  get currentTimezone(): string {
    return this.data.currentTimezone;
  }

  constructor() {
    this.subscriptions.add(this.subscribeToQueryParams());
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  subscribeToQueryParams(): Subscription {
    return this.activeUser$.pipe(take(1)).subscribe(user => {
      const params = {user: user._id};
      this.store$.dispatch(
        new GetAutoSkeds({
          params,
          onSuccess: () => {
            this.loadingGetAutoSkeds.set(false);
          },
          onFailure: () => this.loadingGetAutoSkeds.set(false),
        })
      );
    });
  }

  onSwitch(): void {
    this.loadingSwtich.set(true);
    this.getCurrentUserAndUpdateTimezone();
  }

  private getCurrentUserAndUpdateTimezone(): void {
    this.activeUser$
      .pipe(
        filter((user): user is User => !!user),
        take(1)
      )
      .subscribe(user => this.updateUserTimezone(user));
  }

  private updateUserTimezone(user: User): void {
    const updatedUser: User = {
      ...user,
      timezone: this.data.newTimezone,
    };

    this.store$.dispatch(
      new UpdateUserProfileAction({
        user: updatedUser,
        onSuccess: () => this.onUpdateSuccess(),
        onFailure: () => this.onUpdateFailure(),
      })
    );
  }

  private onUpdateSuccess(): void {
    if (this.changeSkeds()) {
      // Get the current autoSkeds from the store and update their timezones
      this.autoSkeds$.pipe(take(1)).subscribe(autoSkeds => {
        // Update the timezone in each autosked's rrule
        const updatedAutoSkeds = autoSkeds.map(autoSked => ({
          ...autoSked,
          rrule: autoSked.rrule
            ? this.rruleService.updateRRuleTimezone(autoSked.rrule, this.data.newTimezone)
            : autoSked.rrule,
        }));

        this.store$.dispatch(
          new UpdateSkedsWithNewTimezoneAction({
            autoSkeds: updatedAutoSkeds,
            onSuccess: () => {
              this.loadingSwtich.set(false);
              this.store$.dispatch(new UpdateScheduleTimezoneAction({timezone: this.data.newTimezone}));
              window.location.reload();
            },
            onFailure: () => {
              this.loadingSwtich.set(false);
              this.messageService.add('Failed to update timezone. Please try again.', 'error');
            },
          })
        );
      });
    } else {
      this.loadingSwtich.set(false);
      window.location.reload();
    }
  }

  private onUpdateFailure(): void {
    this.loadingSwtich.set(false);
    this.messageService.add('Failed to update timezone. Please try again.', 'error');
  }

  onKeep(): void {
    this.dialogRef.close('keep');
  }

  onCheckbox(checked: boolean): void {
    this.changeSkeds.set(checked);
  }

  onLearnMore(): void {
    this.showTooltip.set(!this.showTooltip());
  }
}
